import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Fade,
    Chip,
    LinearProgress
} from '@mui/material';
import {
    CloudUpload,
    Description,
    CheckCircle,
    Error as ErrorIcon,
    AttachMoney
} from '@mui/icons-material';

function FileUpload() {
    // Transaction Upload State
    const [transactionFile, setTransactionFile] = useState(null);
    const [accountNumber, setAccountNumber] = useState('');
    const [accountNumbers, setAccountNumbers] = useState([]);
    const [transactionUploading, setTransactionUploading] = useState(false);
    const [transactionSuccess, setTransactionSuccess] = useState('');
    const [transactionError, setTransactionError] = useState('');

    // Load account numbers on mount
    useEffect(() => {
        api.get(`/api/csv/accounts`)
            .then(response => {
                setAccountNumbers(response.data);
            })
            .catch(error => {
                console.error('Error fetching account numbers:', error);
                setTransactionError('Failed to load account numbers. Please refresh the page.');
            });
    }, []);

    // Handle Transaction File Selection
    const handleTransactionFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.name.endsWith('.csv')) {
                setTransactionError('Please select a CSV file');
                return;
            }
            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                setTransactionError('File size exceeds 10MB limit');
                return;
            }
            setTransactionFile(file);
            setTransactionError('');
            setTransactionSuccess('');
        }
    };

    // Upload Transaction File
    const handleTransactionUpload = () => {
        if (!transactionFile) {
            setTransactionError('Please select a file to upload');
            return;
        }
        if (!accountNumber) {
            setTransactionError('Please select an account number');
            return;
        }

        setTransactionUploading(true);
        setTransactionError('');
        setTransactionSuccess('');

        const formData = new FormData();
        formData.append('file', transactionFile);
        formData.append('accountNumber', accountNumber);

        api.post(`/api/csv/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then(response => {
                setTransactionSuccess(response.data.message || 'File uploaded successfully!');
                setTransactionFile(null);
                setAccountNumber('');
                // Reset file input
                document.getElementById('transaction-file-input').value = '';
            })
            .catch(error => {
                console.error('Error uploading transaction file:', error);
                const errorMessage = error.response?.data?.message || error.message || 'Failed to upload file';
                setTransactionError(errorMessage);
            })
            .finally(() => {
                setTransactionUploading(false);
            });
    };

    const UploadCard = ({ title, description, icon, children, gradient }) => (
        <Card
            sx={{
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                }
            }}
        >
            <Box
                sx={{
                    background: gradient,
                    color: 'white',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        p: 1.5,
                        display: 'flex'
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {description}
                    </Typography>
                </Box>
            </Box>
            <CardContent sx={{ p: 3 }}>
                {children}
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            py: 4
        }}>
            <Container maxWidth="xl">
                {/* Hero Section */}
                <Fade in timeout={800}>
                    <Box>
                        <Paper
                            elevation={0}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                borderRadius: 4,
                                p: 4,
                                mb: 4
                            }}
                        >
                            <Box display="flex" alignItems="center" mb={1}>
                                <CloudUpload sx={{ fontSize: 40, mr: 2 }} />
                                <Typography variant="h3" fontWeight={700}>
                                    Import Data
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                Upload bank statements to process transactions
                            </Typography>
                        </Paper>
                    </Box>
                </Fade>

                <Grid container spacing={4}>
                    {/* Bank Transaction Upload */}
                    <Grid item xs={12}>
                        <Fade in timeout={1000}>
                            <Box>
                                <UploadCard
                                    title="Bank Transactions"
                                    description="Upload bank statement CSV files"
                                    icon={<AttachMoney sx={{ fontSize: 32 }} />}
                                    gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                                >
                                <Box>
                                    {/* Account Number Selection */}
                                    <FormControl fullWidth sx={{ mb: 3 }}>
                                        <InputLabel>Account Number</InputLabel>
                                        <Select
                                            value={accountNumber}
                                            label="Account Number"
                                            onChange={(e) => setAccountNumber(e.target.value)}
                                            disabled={transactionUploading}
                                        >
                                            <MenuItem value="">
                                                <em>Select an Account</em>
                                            </MenuItem>
                                            {accountNumbers.map((num) => (
                                                <MenuItem key={num} value={num}>
                                                    {num}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* File Upload Button */}
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<Description />}
                                        sx={{
                                            mb: 2,
                                            py: 2,
                                            borderStyle: 'dashed',
                                            borderWidth: 2,
                                            borderColor: transactionFile ? '#11998e' : 'grey.400',
                                            color: transactionFile ? '#11998e' : 'text.primary',
                                            '&:hover': {
                                                borderColor: '#11998e',
                                                backgroundColor: 'rgba(17, 153, 142, 0.05)'
                                            }
                                        }}
                                    >
                                        <input
                                            id="transaction-file-input"
                                            type="file"
                                            accept=".csv"
                                            hidden
                                            onChange={handleTransactionFileChange}
                                            disabled={transactionUploading}
                                        />
                                        {transactionFile ? transactionFile.name : 'Choose CSV File'}
                                    </Button>

                                    {transactionFile && (
                                        <Chip
                                            label={`${(transactionFile.size / 1024).toFixed(2)} KB`}
                                            size="small"
                                            color="success"
                                            sx={{ mb: 2 }}
                                        />
                                    )}

                                    {/* Upload Button */}
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        onClick={handleTransactionUpload}
                                        disabled={transactionUploading || !transactionFile || !accountNumber}
                                        startIcon={transactionUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                                        sx={{
                                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                            py: 1.5,
                                            fontWeight: 600,
                                            boxShadow: '0 4px 15px rgba(17, 153, 142, 0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
                                                boxShadow: '0 6px 20px rgba(17, 153, 142, 0.6)'
                                            },
                                            '&:disabled': {
                                                background: 'grey.300'
                                            }
                                        }}
                                    >
                                        {transactionUploading ? 'Uploading...' : 'Upload Transaction CSV'}
                                    </Button>

                                    {transactionUploading && (
                                        <LinearProgress sx={{ mt: 2 }} />
                                    )}

                                    {/* Success/Error Messages */}
                                    {transactionSuccess && (
                                        <Alert
                                            severity="success"
                                            icon={<CheckCircle />}
                                            sx={{ mt: 2 }}
                                            onClose={() => setTransactionSuccess('')}
                                        >
                                            {transactionSuccess}
                                        </Alert>
                                    )}
                                    {transactionError && (
                                        <Alert
                                            severity="error"
                                            icon={<ErrorIcon />}
                                            sx={{ mt: 2 }}
                                            onClose={() => setTransactionError('')}
                                        >
                                            {transactionError}
                                        </Alert>
                                    )}

                                    {/* File Format Info */}
                                    <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            Required CSV Format:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            • Date, Description, Amount, Running Balance
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            • Maximum file size: 10MB
                                        </Typography>
                                    </Paper>
                                </Box>
                            </UploadCard>
                            </Box>
                        </Fade>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

export default FileUpload;

